/**
 * @file  logger/index.ts
 * @description  logger service to log client side errors
 *
 * @author ogarcia (ozkary)
 *
 */
import {
    ApplicationInsights,
    SeverityLevel
} from '@microsoft/applicationinsights-web';
import ReactGA from 'react-ga4';

const appInsights = new ApplicationInsights({
  config: {
    instrumentationKey: process.env.REACT_APP_Insights,
    enableAutoRouteTracking: true,
    autoTrackPageVisitTime: true,
    isCookieUseDisabled: true,
    isStorageUseDisabled: true,
    enableSessionStorageBuffer: true
  }
});

/**
 * initialize the app insight service
 */
const init = async (): Promise<void> => {
  if (process.env.REACT_APP_Insights && appInsights) {
    appInsights.loadAppInsights();
    appInsights.trackPageView();
    appInsights.context.application.ver = process.env.REACT_APP_VERSION ?? '';
    appInsights.context.application.build = process.env.REACT_APP_BRAND ?? '';
  }
};

/**
 * Track error, events to the server
 * @param message the message or event to log
 * @param level severity level use info for audit
 * @param stack exception data
 */
const serverLog = async (
  message: string,
  level: SeverityLevel,
  stack?: string
): Promise<void> => {
  const exception = {
    error: {
      name: window.location.pathname,
      message,
      stack,
      userAgent: navigator.userAgent
    },
    severityLevel: level
  };

  if (appInsights) {
    switch (level) {
      case SeverityLevel.Error:
        appInsights.trackException(exception);
        break;
      case SeverityLevel.Information:
      case SeverityLevel.Warning:
      case SeverityLevel.Verbose:
        appInsights.trackEvent({ name: window.location.pathname }, { message });
        break;
    }
  }

  if (process.env.REACT_APP_DEBUG) {
    console.log(message, level);
  }
};

/**
 * call to log errors. information will be sent to the server
 * use for catch operations
 * @param error error Error | unknown
 */
const error = (error: Error | unknown): void => {
  if (process.env.REACT_APP_DEBUG) {
    console.log(error);
  } else {
    const err = error as Error;
    serverLog(err.message, SeverityLevel.Error, err.stack);
  }
};

/**
 * tracks informational messages
 * @param msg informational message
 */
const info = (msg: string): void => {
  serverLog(msg, SeverityLevel.Information);
};

/**
 * logs a warning message. no fatal errors
 * @param msg message
 */
const warn = (msg: string): void => {
  serverLog(msg, SeverityLevel.Warning);
};

/**
 * logs a message. This is disabled when debug = false
 * @param msg message
 */
const trace = (msg: string): void => {
  if (process.env.REACT_APP_DEBUG) {
    console.trace(msg, 'trace');
  }
};

/**
 * Audit log a json document for reporting
 * @name the dimension to report
 * @param doc the json document to log
 * @gaDoc non-PII data for google analytics
 */
const audit = <T>(name: string, doc: T, gaDoc: T | null = null): void => {
  try {
    let msg = JSON.stringify(doc);

    if (appInsights) {
      appInsights.trackEvent({ name }, { message: msg });

      if (gaDoc) {
        msg = JSON.stringify(gaDoc);
      }
      ReactGA.event({
        category: 'App Events',
        action: name,
        label: msg,
        nonInteraction: true, // avoids affecting bounce rate
        transport: 'beacon'
      });
    }
  } catch (err: unknown) {
    const msg = JSON.stringify(err);
    serverLog(msg, SeverityLevel.Error, name);
  }
};

/**
 * tracks events including page and other dim tracking events
 * @param event the event or page name which is track as a dimension
 * @param isPageVisitEvent when true it is a page visit
 */
const track = (event: string, isPageVisitEvent = true): void => {
  // sets a new dim to report on
  ReactGA.set({ event });

  // dimension name
  const dim = 'event';

  if (isPageVisitEvent) {
    ReactGA.send({ hitType: 'pageview', page: event, title: event });
    appInsights.trackPageView({ name: event, uri: event });
    return;
  }
  // send to appInsights
  audit<string>(dim, event);
};

/** exported operations */
export { error, info, warn, trace, audit, track };
init();
